/**
 * CreateLink Component
 * 
 * Administrative tool for generating secure invitation links.
 * Creates encoded links with tokens and personalized message templates.
 * Provides CSV export functionality for batch processing.
 */

import React, { useState } from 'react';
import '../App.css';
import './CreateLink.css';

// Shared Components
import ProtectedPageLayout from '../components/shared/ProtectedPageLayout';
import HeroSection from '../components/shared/HeroSection';
import GradientContainer from '../components/shared/GradientContainer';

// Specific Components and Utilities
import { getTemplateOptions, getTemplateById, DEFAULT_TEMPLATE } from '../data/messageTemplates';
import { generateHeroDateFromEvents } from '../utils/dateFormatting';
import { getAllEvents } from '../data/eventsData';

function CreateLink() {
  // Form State Management
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    redirectPath: '/maagiennda',
    expiryDate: '2025-11-30'
  });
  
  // Template and Generation State
  const [selectedTemplate, setSelectedTemplate] = useState(DEFAULT_TEMPLATE.id);
  const [generatedLinks, setGeneratedLinks] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Generate dynamic hero date from all events
  const allEvents = getAllEvents();
  const heroDate = generateHeroDateFromEvents(allEvents);

  // Dynamic Base URL - derives from current page URL
  const getBaseUrl = () => {
    return `${window.location.protocol}//${window.location.host}/`;
  };

  /**
   * URL-safe Base64 Encoding
   * Replaces URL-unsafe characters for secure transmission
   */
  const encode = (data) => {
    return btoa(data).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };

  /**
   * Secure Token Generation
   * Creates 22-character random token for link security
   */
  const generateToken = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
    let result = '';
    for (let i = 0; i < 22; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  /**
   * Generate Secure Invitation Link
   * Creates complete URL with encoded parameters and expiration
   */
  const generateLink = (firstName, lastName, pwd, redirectPath, expiryDate) => {
    const token = generateToken();
    const fullName = `${firstName} ${lastName}`.trim();
    const encodedName = encode(fullName);
    const encodedPwd = encode(pwd);
    const encodedRedirect = encode(redirectPath);
    
    // Create expiration datetime (23:59:00 PST on selected date)
    const expirationDateTime = new Date(expiryDate + 'T23:59:00-08:00');
    const expiration = expirationDateTime.toISOString();
    const encodedExpiration = encode(expiration);

    const fullUrl = `${getBaseUrl()}?token=${token}&name=${encodedName}&pwd=${encodedPwd}&redirect=${encodedRedirect}&expires=${encodedExpiration}`;

    return {
      token,
      expiration,
      fullUrl,
      firstName,
      lastName,
      fullName,
      pwd,
      redirectPath,
      expiryDate
    };
  };

  /**
   * Form Input Change Handler
   * Updates form state for controlled components
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /**
   * Link Generation Handler
   * Validates form data and generates new invitation link
   */
  const handleGenerateLink = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.password.trim()) {
      alert('Please fill in first name, last name, and password fields.');
      return;
    }

    // Validate expiry date (allow today since links expire at 23:59 PST)
    const selectedDate = new Date(formData.expiryDate + 'T23:59:00-08:00');
    const currentDate = new Date();
    if (selectedDate <= currentDate) {
      alert('Selected date has already passed (links expire at 23:59 PST).');
      return;
    }

    setIsGenerating(true);
    
    // Generate link data
    const linkData = generateLink(
      formData.firstName, 
      formData.lastName, 
      formData.password, 
      formData.redirectPath, 
      formData.expiryDate
    );
    
    // Add to generated links list
    setGeneratedLinks(prev => [...prev, linkData]);
    
    // Reset form for next entry
    setFormData({
      firstName: '',
      lastName: '',
      password: '',
      redirectPath: '/maagiennda',
      expiryDate: '2025-11-30'
    });
    
    setIsGenerating(false);
  };

  /**
   * Copy Link to Clipboard
   * Provides user feedback on successful copy
   */
  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link).then(() => {
      alert('Link copied to clipboard!');
    });
  };

  /**
   * Generate Personalized Message
   * Creates message using selected template and link data
   */
  const generatePersonalizedMessage = (firstName, password, fullUrl, redirectPath, templateId = selectedTemplate) => {
    const template = getTemplateById(templateId);
    return template.template(firstName, password, fullUrl, redirectPath);
  };

  /**
   * Copy Message to Clipboard
   * Copies formatted message for sharing
   */
  const handleCopyMessage = (message) => {
    navigator.clipboard.writeText(message).then(() => {
      alert('Personalized message copied to clipboard!');
    });
  };

  /**
   * CSV Export Handler
   * Downloads all generated links as CSV file
   */
  const handleDownloadCSV = () => {
    if (generatedLinks.length === 0) {
      alert('No links to download.');
      return;
    }

    // Prepare CSV data
    const csvHeaders = ['First Name', 'Last Name', 'Full Name', 'Password', 'Token', 'Redirect Path', 'Expiry Date', 'Expires At (UTC)', 'Final Link'];
    const csvRows = generatedLinks.map(link => [
      link.firstName,
      link.lastName,
      link.fullName,
      link.pwd,
      link.token,
      link.redirectPath,
      link.expiryDate,
      link.expiration,
      link.fullUrl
    ]);

    // Generate CSV content
    const csvContent = [csvHeaders, ...csvRows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    // Download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `secure_links_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Redirect Path Options for Dropdown
  const redirectOptions = [
    { value: '/maagiennda', label: 'Main Agenda' },
    { value: '/cweerdedmionngy', label: 'Wedding Day' },
    { value: '/rfeucnecpttiioonn', label: 'Reception' },
    { value: '/swaendgdeientg', label: 'Sangeet & Wedding' },
    { value: '/sngwdrcp8472mjk', label: 'Main Events (Sangeet, Wedding, Reception)' },
    { value: '/wiendvdiitnagtion', label: 'Invitation' },
    { value: '/fllagnd8293kxm', label: 'Full Agenda' },
    { value: '/tmlne4739hjk', label: 'Timeline' },
    { value: '/cmbndgnd8392plw', label: 'Combined Agenda' }
  ];

  return (
    <ProtectedPageLayout authPath="/crtlnk9472xwz" useScrollAnimations={false}>
      {/* Hero Section */}
      <HeroSection 
        subtitle="Create Invitations" 
        date={heroDate} 
      />

      <GradientContainer variant="createlink">
        {/* Link Generation Form */}
        <div className="location-section">
          <div className="location-container">
            <div className="location-title">
              <h2>Personalized Invitation Link Generator</h2>
            </div>
            
            <form onSubmit={handleGenerateLink} className="create-link-form">
              {/* Name Fields - Side by Side */}
              <div className="form-group form-group-inline">
                <div className="form-field-half">
                  <label htmlFor="firstName">First Name:</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div className="form-field-half">
                  <label htmlFor="lastName">Last Name:</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Enter last name"
                    required
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="form-group">
                <label htmlFor="password">Password:</label>
                <input
                  type="text"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter password"
                  required
                />
              </div>

              {/* Redirect Path Selection */}
              <div className="form-group">
                <label htmlFor="redirectPath">Redirect to Page:</label>
                <select
                  id="redirectPath"
                  name="redirectPath"
                  value={formData.redirectPath}
                  onChange={handleInputChange}
                >
                  {redirectOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Expiry Date */}
              <div className="form-group">
                <label htmlFor="expiryDate">Expiry Date:</label>
                <input
                  type="date"
                  id="expiryDate"
                  name="expiryDate"
                  value={formData.expiryDate}
                  onChange={handleInputChange}
                  required
                />
                <small>Links will expire at 23:59 PST on the selected date</small>
              </div>

              {/* Message Template Selection */}
              <div className="form-group">
                <label htmlFor="messageTemplate">Message Template:</label>
                <select
                  id="messageTemplate"
                  name="messageTemplate"
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                >
                  {getTemplateOptions().map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Submit Button */}
              <button type="submit" disabled={isGenerating} className="submit-button">
                {isGenerating ? 'Generating...' : '✅ Generate Link'}
              </button>
            </form>
          </div>
        </div>

        {/* Generated Links Display Section */}
        {generatedLinks.length > 0 && (
          <div className="agenda">
            {/* Section Header with CSV Export */}
            <div className="day-section">
              <div className="day-header">
                <h2>Generated Links ({generatedLinks.length})</h2>
                <button onClick={handleDownloadCSV} className="submit-button">
                  📁 Download CSV
                </button>
              </div>
            </div>

            {/* Generated Links Cards */}
            <div className="generated-links">
              {generatedLinks.slice().reverse().map((link, index) => (
                <div key={generatedLinks.length - 1 - index} className="link-card">
                  {/* Link Header */}
                  <div className="link-header">
                    <h3>{link.fullName}</h3>
                    <span className="link-redirect">
                      {redirectOptions.find(opt => opt.value === link.redirectPath)?.label}
                    </span>
                  </div>
                  
                  {/* Link Details */}
                  <div className="link-details">
                    <p><strong>Password:</strong> {link.pwd}</p>
                    <p><strong>Token:</strong> {link.token}</p>
                    <p><strong>Expiry Date:</strong> {link.expiryDate}</p>
                    <p><strong>Expires At:</strong> {new Date(link.expiration).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })} PST</p>
                  </div>
                  
                  {/* Generated URL */}
                  <div className="link-url">
                    <input
                      type="text"
                      value={link.fullUrl}
                      readOnly
                      className="link-input"
                    />
                    <button
                      onClick={() => handleCopyLink(link.fullUrl)}
                      className="copy-button"
                    >
                      📋 Copy
                    </button>
                  </div>
                  
                  {/* Personalized Message Section */}
                  <div className="personalized-message-section">
                    <h4>Personalized Message</h4>
                    
                    {/* Clickable Link Preview */}
                    <div className="clickable-link-section">
                      <label>Clickable Link Preview:</label>
                      <a 
                        href={link.fullUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="clickable-invitation-link"
                      >
                        {link.fullUrl}
                      </a>
                    </div>
                    
                    {/* Message Textarea */}
                    <textarea
                      value={generatePersonalizedMessage(link.firstName, link.pwd, link.fullUrl, link.redirectPath)}
                      readOnly
                      className="message-textarea"
                      rows="16"
                      placeholder="Copy-friendly message..."
                    />
                    
                    {/* Copy Message Button */}
                    <button
                      onClick={() => handleCopyMessage(generatePersonalizedMessage(link.firstName, link.pwd, link.fullUrl, link.redirectPath))}
                      className="copy-button message-copy-button"
                    >
                      📋 Copy Message
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </GradientContainer>
    </ProtectedPageLayout>
  );
}

export default CreateLink;